import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import {Dispatch} from "react";
import {resetSelectedBoxValues} from './Utils/ResetSelectBoxValues';

interface Address {
    address: string;
}

interface trashType {
    name: string;
    id: string;
}

interface containerSize {
    id: string;
    name: string;
    photo: string
}

interface trashTypeWithSizesMap {
}

const sleep = (ms: number | undefined) =>
    new Promise(resolve => setTimeout(resolve, ms));

function fetchAddress(textInput: string) {
    return new Promise<{
        options: Address[],
    }>((resolve, reject) => {

        const params = {
            textInput: textInput,
        };
        const options = {
            method: 'POST',
            body: JSON.stringify(params)
        };

        fetch('/address-autocomplete', options)
            .then((res) => res.json())
            .then(({options}) => {
                resolve({options});
            });
    });
}

function fetchContainersOptions(address: string, setShowLoader: any) {
    return new Promise<{
        trashTypes: trashType[],
        containerSizes: containerSize[],
        trashTypesWithSizesMap: trashTypeWithSizesMap[]
    }>((resolve, reject) => {

        const params = {
            address: address,
        };
        const options = {
            method: 'POST',
            body: JSON.stringify(params)
        };
        setShowLoader('show');
        fetch('/containers-options', options)
            .then((res) => res.json())
            .then(({trashTypes, containerSizes, trashTypesWithSizesMap}) => {
                resolve({trashTypes, containerSizes, trashTypesWithSizesMap});
                setShowLoader('hide');
            });
    });
}

type addressFieldProps = {
    setAddress: Dispatch<any>,
    setTrashTypes: Dispatch<any>,
    setContainerSizes: Dispatch<any>,
    setWithValues: Dispatch<any>,
    setWithoutValues: Dispatch<any>,
    setTrashType: Dispatch<any>,
    setContainerSize: Dispatch<any>,
    setTrashTypesWithSizesMap: Dispatch<any>,
    setShowLoader: Dispatch<any>,
    translations: any,
    setContainersNotFound: Dispatch<any>,
    addressError: boolean,
    selectedAddress: any
};

export const AddressField = ({
                                 setAddress,
                                 setTrashTypes,
                                 setContainerSizes,
                                 setWithValues,
                                 setWithoutValues,
                                 setTrashType,
                                 setContainerSize,
                                 setTrashTypesWithSizesMap,
                                 setShowLoader,
                                 translations,
                                 setContainersNotFound,
                                 addressError,
                                 selectedAddress
                             }: addressFieldProps) => {
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<readonly Address[]>([]);
    const loading = open && options.length === 0;
    const requestAbortController = React.useRef<AbortController | null>(null);
    const [value, setValue] = React.useState<Address | null>(null);
    const [inputValue, setInputValue] = React.useState('');

    const fetchAddressOptions = (textInput: string) => {
        const controller = new AbortController();
        fetchAddress(textInput)
            .then(({options}) => {
                setOptions([...options]);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    throw error;
                }
            });

        requestAbortController.current = controller;
    };

    const loadContainersOptions = (address: string) => {
        const controller = new AbortController();
        fetchContainersOptions(address, setShowLoader)
            .then(({trashTypes, containerSizes, trashTypesWithSizesMap}) => {
                resetSelectedBoxValues('trashType');
                resetSelectedBoxValues('containerSize');
                setTrashType('');
                setContainerSize('');
                setWithValues(false);
                setWithoutValues(true);
                setTrashTypes(trashTypes);
                setContainerSizes(containerSizes);
                setTrashTypesWithSizesMap(trashTypesWithSizesMap);
                if (trashTypes.length == 1) {
                    setContainersNotFound(true);
                } else {
                    setContainersNotFound(false);
                }
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    throw error;
                }
            });

        requestAbortController.current = controller;
    };

    React.useEffect(() => {
        if (selectedAddress != '') {
            setValue({address: selectedAddress});
            setInputValue(selectedAddress);
        }
    }, [selectedAddress]);

    React.useEffect(() => {
        let active = true;
        if (inputValue.length > 3) {
            loadContainersOptions(inputValue);
        }
        return () => {
            active = false;
        };
    }, [value]);


    React.useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            let active = true;

            if (active && inputValue.length > 3) {
                fetchAddressOptions(inputValue);
            }

            return () => {
                active = false;
            };
            // Send Axios request here
        }, 700)

        return () => clearTimeout(delayDebounceFn)
    }, [value, inputValue]);


    React.useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleChange = (e: any) => {
        e.preventDefault();
    };

    return (
        <Autocomplete
            id="addressField"
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            className={addressError ? 'addressError' : ''}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText={translations.address_not_found}
            loadingText={translations.search_address}
            onChange={(event: any, newValue: Address | null) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
            }}
            onInputChange={(event, newInputValue) => {
                setAddress(newInputValue);
                setInputValue(newInputValue);
            }}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option.address}
            options={options}
            loading={loading}
            renderInput={(params) => (
                <TextField
                    onPaste={handleChange}
                    onCut={handleChange}
                    onCopy={handleChange}
                    placeholder={translations.address}
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}
